import styled from '@emotion/styled'
import { useDebouncedCallback } from '@mantine/hooks'
import { FunctionComponent, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Button from '~/components/Button'
import { BaseInputElement } from '~/components/Input'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { UnstyledSelect } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
`

// Constants
const SEARCH_DEBOUNCE_TIME_MILLIS = 300

type Props = {
  /** Left out where the page filters by something other than a status — the dropdown then never renders. */
  statusOptions?: SelectOptionProp[]
  onSearchChange: (search: string) => void
  onStatusChange?: (status: string) => void
  resetFiltersTrackingLabel: TrackingLabel
  typeOptions?: SelectOptionProp[]
  typeLabel?: string
  onTypeChange?: (type: string) => void
  /** Runs inside the reset, so a page holding filters of its own can clear them and drop their params
      from the *same* `URLSearchParams` this writes. A page that calls `setSearchParams` itself from a
      reset callback instead builds it from the pre-click snapshot, and the second write puts back
      every param this one just deleted. */
  onResetFilters?: (params: URLSearchParams) => void
  /**
   * Widens the search box, for the pages that give it a row of its own rather than a share of a filter
   * row. Opt-in: the pages that put two selects beside it want it the size it has always been.
   */
  searchClassName?: string
}

export const FilterBar: FunctionComponent<Props> = ({
  statusOptions,
  onSearchChange,
  onStatusChange,
  resetFiltersTrackingLabel,
  typeOptions,
  typeLabel = 'Type',
  onTypeChange,
  onResetFilters,
  searchClassName,
}) => {
  // Context
  const [searchParams, setSearchParams] = useSearchParams()

  // State
  const [search, setSearch] = useState<string>(searchParams.get('search') || '')
  const [status, setStatus] = useState<string>(searchParams.get('status') || 'all')
  const [type, setType] = useState<string>(searchParams.get('type') || 'all')

  const debouncedOnSearchChange = useDebouncedCallback(onSearchChange, SEARCH_DEBOUNCE_TIME_MILLIS)
  const debouncedSetSearchParams = useDebouncedCallback(setSearchParams, SEARCH_DEBOUNCE_TIME_MILLIS)

  // Functional updates throughout: `searchParams` is a snapshot from render time, and `handleSearchChange`
  // flushes 300ms later through `debouncedSetSearchParams`. Building `params` from that stale snapshot
  // instead of the URL at flush time silently reverted whatever another writer — e.g. the page's own tab
  // state — had pushed to the URL in between, snapping the UI back to that param's default.
  const handleSearchChange = (e: string) => {
    setSearch(e)

    debouncedSetSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (!e) params.delete('search')
      else params.set('search', e)
      return params
    })

    debouncedOnSearchChange(e)
  }

  const handleStatusChange = (e: string) => {
    setStatus(e)

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (e === 'all') params.delete('status')
      else params.set('status', e)
      return params
    })

    onStatusChange?.(e)
  }

  const handleTypeChange = (e: string) => {
    setType(e)

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (e === 'all') params.delete('type')
      else params.set('type', e)
      return params
    })

    onTypeChange?.(e)
  }

  const handleResetFilters = () => {
    setSearch('')
    setStatus('all')
    setType('all')

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.delete('search')
      params.delete('status')
      params.delete('type')
      onResetFilters?.(params)
      return params
    })

    onSearchChange('')
    onStatusChange?.('all')
    onTypeChange?.('all')
  }

  return (
    <Wrapper>
      {searchClassName ? (
        <div className={searchClassName}>
          <BaseInputElement type="text" iconLeft="search" label="Search" labelIsPlaceholder value={search} onChange={(e) => handleSearchChange(e.target.value)} />
        </div>
      ) : (
        <BaseInputElement type="text" iconLeft="search" label="Search" labelIsPlaceholder value={search} onChange={(e) => handleSearchChange(e.target.value)} />
      )}
      {statusOptions && <UnstyledSelect label="Status" options={statusOptions} value={status} onChange={(e) => handleStatusChange(e.value as string)} />}
      {typeOptions && <UnstyledSelect label={typeLabel} options={typeOptions} value={type} onChange={(e) => handleTypeChange(e.value as string)} />}
      <Button iconName="close" variant="tertiary" onClick={handleResetFilters} trackingLabel={resetFiltersTrackingLabel} />
    </Wrapper>
  )
}
